import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { LiveArena, ArenaParticipant } from "@/hooks/useLiveArenas";
import { useGeoData } from "@/hooks/useGeoData";

export interface ExhibitionCountry {
  code: string;
  name: string;
  flag: string;
  cityCount: number;
  boothCount: number;
  participantCount: number;
  isHot?: boolean;
  hasStates?: boolean; // For countries like US that have states
}

export interface ExhibitionState {
  id: string;
  code: string;
  name: string;
  country_code: string;
  cityCount: number;
  boothCount: number;
  participantCount: number;
  isHot?: boolean;
}

export interface ExhibitionCity {
  id: string;
  name: string;
  country_code: string;
  state_id?: string | null;
  state_name?: string;
  boothCount: number;
  participantCount: number;
  hotBooths: number;
  previewFaces: string[];
  isHot?: boolean;
}

export interface ExhibitionBooth extends LiveArena {
  is_exhibition_booth: boolean;
  exhibition_intent: string | null;
  host_name?: string;
  host_photo?: string;
  featured_participants?: {
    id: string;
    name: string;
    photo_url: string | null;
    video_snippet_url?: string;
  }[];
}

export interface JoinRequest {
  id: string;
  user_id: string;
  booth_id: string;
  status: string;
  request_date: string;
  created_at: string;
  responded_at: string | null;
  booth?: {
    name: string;
    topic: string;
  };
  user?: {
    name: string;
    photo_url: string | null;
  };
}

export const useExhibition = () => {
  const { user } = useAuthContext();
  const { getCountries, getStates, getCities } = useGeoData();
  const [countries, setCountries] = useState<ExhibitionCountry[]>([]);
  const [states, setStates] = useState<ExhibitionState[]>([]);
  const [cities, setCities] = useState<ExhibitionCity[]>([]);
  const [booths, setBooths] = useState<ExhibitionBooth[]>([]);
  const [currentBooth, setCurrentBooth] = useState<ExhibitionBooth | null>(null);
  const [participants, setParticipants] = useState<ArenaParticipant[]>([]);
  const [myRequests, setMyRequests] = useState<JoinRequest[]>([]);
  const [pendingRequests, setPendingRequests] = useState<JoinRequest[]>([]);
  const [remainingRequests, setRemainingRequests] = useState(5);
  const [loading, setLoading] = useState(true);

  // Fetch remaining join requests for today
  const fetchRemainingRequests = useCallback(async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .rpc("get_remaining_join_requests", { user_uuid: user.id });
    
    if (!error && data !== null) {
      setRemainingRequests(data);
    }
  }, [user]);

  // Fetch countries with exhibition stats - using useGeoData
  const fetchCountries = useCallback(async () => {
    setLoading(true);
    try {
      console.log("🌍 [EXHIBITION] Fetching countries from useGeoData...");
      
      // Get countries from useGeoData (uses geo_countries table)
      const geoCountries = await getCountries();
      
      if (!geoCountries || geoCountries.length === 0) {
        console.warn("⚠️ [EXHIBITION] No countries returned from useGeoData");
        setCountries([]);
        setLoading(false);
        return;
      }

      console.log(`✅ [EXHIBITION] Got ${geoCountries.length} countries from useGeoData`);

      // Get booth and city counts from database in parallel
      const [
        { data: boothCountData },
        { data: citiesData },
      ] = await Promise.all([
        supabase.from("live_arenas").select("country_code").eq("is_active", true).eq("is_exhibition_booth", true),
        supabase.from("cities").select("country_code").eq("is_active", true),
      ]);

      // Count booths per country
      const boothsByCountry: Record<string, number> = {};
      boothCountData?.forEach(b => {
        if (b.country_code) {
          boothsByCountry[b.country_code] = (boothsByCountry[b.country_code] || 0) + 1;
        }
      });

      // Count cities per country
      const citiesByCountry: Record<string, number> = {};
      citiesData?.forEach(c => {
        citiesByCountry[c.country_code] = (citiesByCountry[c.country_code] || 0) + 1;
      });

      // Build countries list with stats
      const countriesWithStats: ExhibitionCountry[] = geoCountries.map((geoCountry) => ({
        code: geoCountry.iso2,
        name: geoCountry.name,
        flag: (geoCountry as any).flag || "🏳️",
        cityCount: citiesByCountry[geoCountry.iso2] || 0,
        boothCount: boothsByCountry[geoCountry.iso2] || 0,
        participantCount: 0, // Skip for initial load speed
        isHot: (boothsByCountry[geoCountry.iso2] || 0) > 0,
        hasStates: geoCountry.has_states || false,
      }));

      // Sort by activity (countries with booths first, then by name)
      countriesWithStats.sort((a, b) => {
        if (a.boothCount !== b.boothCount) {
          return b.boothCount - a.boothCount;
        }
        if (a.cityCount !== b.cityCount) {
          return b.cityCount - a.cityCount;
        }
        return a.name.localeCompare(b.name);
      });
      
      console.log(`✅ [EXHIBITION] Set ${countriesWithStats.length} countries with stats`);
      setCountries(countriesWithStats);
    } catch (error) {
      console.error("❌ [EXHIBITION] Error fetching exhibition countries:", error);
    } finally {
      setLoading(false);
    }
  }, [getCountries]);

  // Fetch states for a country (e.g., US) using useGeoData
  const fetchStates = useCallback(async (countryCode: string) => {
    setLoading(true);
    try {
      // Use useGeoData to get states
      const geoStates = await getStates(countryCode);
      
      if (!geoStates || geoStates.length === 0) {
        setStates([]);
        return;
      }
      
      // Convert GeoState to ExhibitionState format
      const statesData = geoStates.map(gs => ({
        id: gs.id,
        code: gs.code || gs.name,
        name: gs.name,
        country_code: gs.country_iso2,
      }));

      // Get all cities for these states at once
      const stateIds = statesData.map(s => s.id);
      const { data: citiesData } = await supabase
        .from("cities")
        .select("id, state_id")
        .in("state_id", stateIds)
        .eq("is_active", true);

      // Count cities per state
      const citiesByState: Record<string, string[]> = {};
      citiesData?.forEach(c => {
        if (c.state_id) {
          if (!citiesByState[c.state_id]) citiesByState[c.state_id] = [];
          citiesByState[c.state_id].push(c.id);
        }
      });

      // Get all active booths for this country
      const { data: boothsData } = await supabase
        .from("live_arenas")
        .select("id, city_id")
        .eq("country_code", countryCode)
        .eq("is_active", true)
        .eq("is_exhibition_booth", true);

      // Map city_id to booth count
      const boothsByCityId: Record<string, number> = {};
      boothsData?.forEach(b => {
        if (b.city_id) {
          boothsByCityId[b.city_id] = (boothsByCityId[b.city_id] || 0) + 1;
        }
      });

      // Get participants for booths in this country
      const boothIds = boothsData?.map(b => b.id) || [];
      let participantsByCityId: Record<string, number> = {};
      
      if (boothIds.length > 0) {
        const { data: participantsData } = await supabase
          .from("arena_participants")
          .select("arena_id, live_arenas!inner(city_id)")
          .in("arena_id", boothIds)
          .is("left_at", null);

        participantsData?.forEach(p => {
          const cityId = (p.live_arenas as any)?.city_id;
          if (cityId) {
            participantsByCityId[cityId] = (participantsByCityId[cityId] || 0) + 1;
          }
        });
      }

      // Build states with stats
      const statesWithStats: ExhibitionState[] = statesData.map((state) => {
        const cityIds = citiesByState[state.id] || [];
        let boothCount = 0;
        let participantCount = 0;

        cityIds.forEach(cityId => {
          boothCount += boothsByCityId[cityId] || 0;
          participantCount += participantsByCityId[cityId] || 0;
        });

        return {
          ...state,
          cityCount: cityIds.length,
          boothCount,
          participantCount,
          isHot: participantCount > 5,
        };
      });

      // Sort by activity
      statesWithStats.sort((a, b) => {
        if (a.participantCount !== b.participantCount) {
          return b.participantCount - a.participantCount;
        }
        if (a.boothCount !== b.boothCount) {
          return b.boothCount - a.boothCount;
        }
        return a.name.localeCompare(b.name);
      });
      
      setStates(statesWithStats);
    } catch (error) {
      console.error("Error fetching exhibition states:", error);
    } finally {
      setLoading(false);
    }
  }, [getStates]);

  // Fetch cities for a country (all cities if no states, or filter by stateId) using useGeoData
  const fetchCities = useCallback(async (countryCode: string, stateId?: string) => {
    setLoading(true);
    try {
      // Get state name if stateId is provided
      let stateName: string | undefined;
      if (stateId) {
        const currentState = states.find(s => s.id === stateId);
        stateName = currentState?.name;
      }

      // Use useGeoData to get ALL cities
      const geoCities = await getCities(countryCode, stateName);
      
      if (!geoCities || geoCities.length === 0) {
        setCities([]);
        return;
      }

      // Get database cities to match with geo cities for stats
      // We'll match by name and country/state
      const { data: dbCities } = await supabase
        .from("cities")
        .select("id, name, country_code, state_id")
        .eq("country_code", countryCode)
        .eq("is_active", true);

      // Create a map of database cities by name (for matching)
      const dbCitiesMap = new Map<string, typeof dbCities[0]>();
      dbCities?.forEach(c => {
        const key = `${c.name.toLowerCase()}-${c.country_code}`;
        if (!dbCitiesMap.has(key)) {
          dbCitiesMap.set(key, c);
        }
      });

      // Match geo cities with database cities
      const citiesData = geoCities.map(gc => {
        const key = `${gc.name.toLowerCase()}-${gc.country_iso2}`;
        const dbCity = dbCitiesMap.get(key);
        return {
          id: dbCity?.id || gc.id, // Use DB ID if available, else geo ID
          name: gc.name,
          country_code: gc.country_iso2,
          state_id: stateId || dbCity?.state_id || null,
        };
      });

      // Get state names for display (if applicable)
      let stateNamesMap: Record<string, string> = {};
      if (!stateId && geoCities.some(c => c.state_name)) {
        // Use state_name from geo cities
        geoCities.forEach(gc => {
          if (gc.state_name) {
            // We'll use the state name directly from geo data
            const matchedCity = citiesData.find(c => c.name === gc.name);
            if (matchedCity && matchedCity.state_id) {
              stateNamesMap[matchedCity.state_id] = gc.state_name;
            }
          }
        });
      } else if (stateId && stateName) {
        // Use the state name we already have
        citiesData.forEach(c => {
          if (c.state_id === stateId) {
            stateNamesMap[c.state_id] = stateName;
          }
        });
      }

      // Get all city IDs for batch queries
      const cityIds = citiesData.map(c => c.id);

      // Get all booths for these cities at once
      const { data: boothsData } = await supabase
        .from("live_arenas")
        .select("id, city_id")
        .in("city_id", cityIds)
        .eq("is_active", true)
        .eq("is_exhibition_booth", true);

      // Count booths per city and collect booth IDs
      const boothsByCityId: Record<string, string[]> = {};
      boothsData?.forEach(b => {
        if (b.city_id) {
          if (!boothsByCityId[b.city_id]) boothsByCityId[b.city_id] = [];
          boothsByCityId[b.city_id].push(b.id);
        }
      });

      // Get all participants for these booths at once
      const allBoothIds = boothsData?.map(b => b.id) || [];
      let participantsByCityId: Record<string, number> = {};
      let facesByCityId: Record<string, string[]> = {};

      if (allBoothIds.length > 0) {
        const { data: participantsData } = await supabase
          .from("arena_participants")
          .select(`
            arena_id,
            profiles:user_id (photo_url),
            live_arenas!inner(city_id)
          `)
          .in("arena_id", allBoothIds)
          .is("left_at", null);

        participantsData?.forEach(p => {
          const cityId = (p.live_arenas as any)?.city_id;
          if (cityId) {
            participantsByCityId[cityId] = (participantsByCityId[cityId] || 0) + 1;
            const photoUrl = (p.profiles as any)?.photo_url;
            if (photoUrl) {
              if (!facesByCityId[cityId]) facesByCityId[cityId] = [];
              if (facesByCityId[cityId].length < 4) {
                facesByCityId[cityId].push(photoUrl);
              }
            }
          }
        });
      }

      // Build cities with stats
      const citiesWithStats: ExhibitionCity[] = citiesData.map((city) => {
        const boothCount = boothsByCityId[city.id]?.length || 0;
        const participantCount = participantsByCityId[city.id] || 0;
        
        return {
          ...city,
          state_name: city.state_id ? stateNamesMap[city.state_id] : undefined,
          boothCount,
          participantCount,
          hotBooths: participantCount > 5 ? 1 : 0,
          previewFaces: facesByCityId[city.id] || [],
          isHot: participantCount > 5,
        };
      });

      // Sort by activity
      citiesWithStats.sort((a, b) => {
        if (a.participantCount !== b.participantCount) {
          return b.participantCount - a.participantCount;
        }
        if (a.boothCount !== b.boothCount) {
          return b.boothCount - a.boothCount;
        }
        return a.name.localeCompare(b.name);
      });
      
      setCities(citiesWithStats);
    } catch (error) {
      console.error("Error fetching exhibition cities:", error);
    } finally {
      setLoading(false);
    }
  }, [getCities, states]);

  // Fetch booths for a city
  const fetchBooths = useCallback(async (cityId: string) => {
    setLoading(true);
    try {
      const { data: boothsData, error } = await supabase
        .from("live_arenas")
        .select(`
          *,
          arena_participants(count),
          profiles:created_by (id, name, photo_url)
        `)
        .eq("city_id", cityId)
        .eq("is_active", true)
        .eq("is_exhibition_booth", true);

      if (error) throw error;

      // Get user's participation and requests
      let userParticipation: string[] = [];
      let userRequests: Record<string, string> = {};
      
      if (user) {
        const { data: participation } = await supabase
          .from("arena_participants")
          .select("arena_id")
          .eq("user_id", user.id)
          .is("left_at", null);
        
        userParticipation = participation?.map(p => p.arena_id) || [];

        const { data: requests } = await supabase
          .from("exhibition_join_requests")
          .select("booth_id, status")
          .eq("user_id", user.id);
        
        requests?.forEach(r => {
          userRequests[r.booth_id] = r.status;
        });
      }

      // Get featured participants for each booth
      const boothsWithData = await Promise.all(
        (boothsData || []).map(async (booth) => {
          const { data: featured } = await supabase
            .from("exhibition_featured_participants")
            .select(`
              profiles:user_id (id, name, photo_url),
              video_snippet_url
            `)
            .eq("booth_id", booth.id)
            .order("display_order")
            .limit(6);

          const hostProfile = booth.profiles as any;

          return {
            ...booth,
            participants_count: booth.arena_participants?.[0]?.count || 0,
            is_joined: userParticipation.includes(booth.id),
            request_status: userRequests[booth.id] || null,
            host_name: hostProfile?.name,
            host_photo: hostProfile?.photo_url,
            featured_participants: featured?.map(f => ({
              ...(f.profiles as any),
              video_snippet_url: f.video_snippet_url,
            })) || [],
          };
        })
      );

      setBooths(boothsWithData);
    } catch (error) {
      console.error("Error fetching exhibition booths:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch booth participants
  const fetchParticipants = useCallback(async (boothId: string) => {
    const { data, error } = await supabase
      .from("arena_participants")
      .select(`
        *,
        profiles:user_id (id, name, photo_url)
      `)
      .eq("arena_id", boothId)
      .is("left_at", null);

    if (!error && data) {
      setParticipants(
        data.map(p => ({
          ...p,
          profile: p.profiles as unknown as ArenaParticipant["profile"],
        }))
      );
    }
  }, []);

  // Create join request - with guard against self-requests
  const requestToJoin = useCallback(async (boothId: string, hostUserId?: string): Promise<boolean> => {
    console.log("requestToJoin: Starting", { boothId, userId: user?.id, remainingRequests, hostUserId });
    
    if (!user) {
      console.error("requestToJoin: No user logged in");
      return false;
    }
    
    // CRITICAL: Prevent host from requesting to join their own booth
    if (hostUserId && user.id === hostUserId) {
      console.error("requestToJoin: Cannot request to join your own booth - you are the host");
      return false;
    }
    
    if (remainingRequests <= 0) {
      console.error("requestToJoin: No remaining requests");
      return false;
    }

    try {
      console.log("requestToJoin: Inserting request into database");
      const { data, error } = await supabase
        .from("exhibition_join_requests")
        .insert({
          user_id: user.id,
          booth_id: boothId,
        })
        .select()
        .single();

      if (error) {
        console.error("requestToJoin: Database error:", error);
        throw error;
      }

      console.log("requestToJoin: Success, request created:", data);
      setRemainingRequests(prev => Math.max(0, prev - 1));
      // Note: fetchMyRequests is called separately after to avoid circular dependency
      return true;
    } catch (error) {
      console.error("requestToJoin: Error creating join request:", error);
      return false;
    }
  }, [user, remainingRequests]);

  // Fetch user's requests
  const fetchMyRequests = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("exhibition_join_requests")
      .select(`
        *,
        booth:booth_id (name, topic)
      `)
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setMyRequests(data.map(r => ({
        ...r,
        booth: r.booth as any,
      })));
    }
  }, [user]);

  // Fetch pending requests for host
  const fetchPendingRequests = useCallback(async (boothId: string) => {
    if (!user) return;

    const { data, error } = await supabase
      .from("exhibition_join_requests")
      .select(`
        *,
        user:user_id (name, photo_url)
      `)
      .eq("booth_id", boothId)
      .eq("status", "pending")
      .order("created_at", { ascending: true });

    if (!error && data) {
      setPendingRequests(data.map(r => ({
        ...r,
        user: r.user as any,
      })));
    }
  }, [user]);

  // Accept request (host)
  const acceptRequest = useCallback(async (requestId: string, boothId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Update request status
      const { error: updateError } = await supabase
        .from("exhibition_join_requests")
        .update({
          status: "accepted",
          responded_at: new Date().toISOString(),
        })
        .eq("id", requestId);

      if (updateError) throw updateError;

      // Get user ID from request
      const request = pendingRequests.find(r => r.id === requestId);
      if (request) {
        // Add user to booth participants
        await supabase
          .from("arena_participants")
          .insert({
            arena_id: boothId,
            user_id: request.user_id,
            is_speaker: false,
            is_muted: true,
            is_video_on: false,
          });

        // Send notification
        await supabase
          .from("notifications")
          .insert({
            user_id: request.user_id,
            type: "booth_accepted",
            title: "You've been accepted! 💖",
            body: `Your request to join the booth has been accepted.`,
            data: { booth_id: boothId },
          });
      }

      await fetchPendingRequests(boothId);
      return true;
    } catch (error) {
      console.error("Error accepting request:", error);
      return false;
    }
  }, [user, pendingRequests, fetchPendingRequests]);

  // Reject request (host)
  const rejectRequest = useCallback(async (requestId: string, boothId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const request = pendingRequests.find(r => r.id === requestId);
      
      const { error } = await supabase
        .from("exhibition_join_requests")
        .update({
          status: "rejected",
          responded_at: new Date().toISOString(),
        })
        .eq("id", requestId);

      if (error) throw error;

      // Send notification
      if (request) {
        await supabase
          .from("notifications")
          .insert({
            user_id: request.user_id,
            type: "booth_rejected",
            title: "Request not accepted",
            body: "Your booth request wasn't accepted. Try similar booths!",
            data: { booth_id: boothId },
          });
      }

      await fetchPendingRequests(boothId);
      return true;
    } catch (error) {
      console.error("Error rejecting request:", error);
      return false;
    }
  }, [user, pendingRequests, fetchPendingRequests]);

  // Create exhibition booth
  const createBooth = useCallback(async (params: {
    name: string;
    topic: string;
    description: string;
    city_id: string;
    country_code: string;
    exhibition_intent: string;
    language: string;
    max_participants: number;
    is_video_enabled: boolean;
  }): Promise<string | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from("live_arenas")
        .insert({
          name: params.name,
          topic: params.topic,
          description: params.description,
          city_id: params.city_id,
          country_code: params.country_code,
          created_by: user.id,
          is_exhibition_booth: true,
          exhibition_intent: params.exhibition_intent,
          language: params.language,
          max_participants: params.max_participants,
          is_video_enabled: params.is_video_enabled,
          is_voice_only: !params.is_video_enabled,
          arena_type: "exhibition",
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;

      // Auto-join as host
      await supabase
        .from("arena_participants")
        .insert({
          arena_id: data.id,
          user_id: user.id,
          is_speaker: true,
          is_muted: false,
          is_video_on: params.is_video_enabled,
        });

      return data.id;
    } catch (error) {
      console.error("Error creating exhibition booth:", error);
      return null;
    }
  }, [user]);

  // Leave booth
  const leaveBooth = useCallback(async (boothId: string) => {
    if (!user) return false;

    try {
      await supabase
        .from("arena_participants")
        .update({ left_at: new Date().toISOString() })
        .eq("arena_id", boothId)
        .eq("user_id", user.id);

      setCurrentBooth(null);
      setParticipants([]);
      return true;
    } catch (error) {
      console.error("Error leaving booth:", error);
      return false;
    }
  }, [user]);

  // End booth (host only)
  const endBooth = useCallback(async (boothId: string) => {
    if (!user) return false;

    try {
      await supabase
        .from("live_arenas")
        .update({
          is_active: false,
          ended_at: new Date().toISOString(),
        })
        .eq("id", boothId)
        .eq("created_by", user.id);

      setCurrentBooth(null);
      setParticipants([]);
      return true;
    } catch (error) {
      console.error("Error ending booth:", error);
      return false;
    }
  }, [user]);

  // Toggle mute
  const toggleMute = useCallback(async (boothId: string) => {
    if (!user) return;

    const participant = participants.find(p => p.user_id === user.id);
    if (!participant) return;

    await supabase
      .from("arena_participants")
      .update({ is_muted: !participant.is_muted })
      .eq("arena_id", boothId)
      .eq("user_id", user.id);

    setParticipants(prev => prev.map(p =>
      p.user_id === user.id ? { ...p, is_muted: !p.is_muted } : p
    ));
  }, [user, participants]);

  // Toggle video
  const toggleVideo = useCallback(async (boothId: string) => {
    if (!user) return;

    const participant = participants.find(p => p.user_id === user.id);
    if (!participant) return;

    await supabase
      .from("arena_participants")
      .update({ is_video_on: !participant.is_video_on })
      .eq("arena_id", boothId)
      .eq("user_id", user.id);

    setParticipants(prev => prev.map(p =>
      p.user_id === user.id ? { ...p, is_video_on: !p.is_video_on } : p
    ));
  }, [user, participants]);

  // Initial fetch
  useEffect(() => {
    fetchRemainingRequests();
  }, [fetchRemainingRequests]);

  // Subscribe to realtime updates for join requests
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("exhibition_requests")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "exhibition_join_requests",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchMyRequests();
          fetchRemainingRequests();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchMyRequests, fetchRemainingRequests]);

  return {
    // Data
    countries,
    states,
    cities,
    booths,
    currentBooth,
    participants,
    myRequests,
    pendingRequests,
    remainingRequests,
    loading,
    // Actions
    fetchCountries,
    fetchStates,
    fetchCities,
    fetchBooths,
    fetchParticipants,
    fetchMyRequests,
    fetchPendingRequests,
    requestToJoin,
    acceptRequest,
    rejectRequest,
    createBooth,
    leaveBooth,
    endBooth,
    toggleMute,
    toggleVideo,
    setCurrentBooth,
  };
};
